import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faKey, faCopy, faCheck } from "@fortawesome/free-solid-svg-icons";
import styles from "./SignInCodePanel.module.css";

const formatCountdown = (totalSeconds) => {
  const safeSeconds = Math.max(0, totalSeconds);
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

/**
 * Reusable panel for generating and displaying a temporary code.
 * Shows a countdown when expiresInSeconds is provided with a code.
 */
const SignInCodePanel = ({
  title = "Generate Sign-in Code",
  description = "Create a temporary code to sign in on this device.",
  code = null,
  expiresInSeconds = 300,
  generateLabel = "Generate Code",
  regenerating = false,
  onGenerate,
  onCopy,
  onExpire,
  copied = false,
}) => {
  const [remainingSeconds, setRemainingSeconds] = useState(expiresInSeconds);

  useEffect(() => {
    if (!code) {
      setRemainingSeconds(expiresInSeconds);
      return undefined;
    }

    setRemainingSeconds(expiresInSeconds);
    const intervalId = window.setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          window.clearInterval(intervalId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [code, expiresInSeconds]);

  useEffect(() => {
    if (code && remainingSeconds === 0) {
      onExpire?.();
    }
  }, [code, remainingSeconds, onExpire]);

  const isExpiringSoon = code && remainingSeconds > 0 && remainingSeconds <= 60;
  const progress =
    expiresInSeconds > 0
      ? Math.max(0, Math.min(1, remainingSeconds / expiresInSeconds))
      : 0;
  const radius = 9;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - progress);

  return (
    <section className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.iconWrap} aria-hidden="true">
          <FontAwesomeIcon icon={faKey} />
        </div>
        <div>
          <h3 className={styles.title}>{title}</h3>
          <p className={styles.description}>{description}</p>
        </div>
      </div>

      {code ? (
        <div className={styles.codeBlock}>
          <div className={styles.codeRow}>
            <span className={styles.code}>{code}</span>
            <button
              type="button"
              className={styles.copyButton}
              onClick={onCopy}
              aria-label="Copy sign-in code"
            >
              <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
              <span>{copied ? "Copied" : "Copy"}</span>
            </button>
          </div>
          <div
            className={`${styles.countdown} ${
              isExpiringSoon ? styles.countdownWarn : ""
            }`}
            aria-live="polite"
          >
            <svg
              className={styles.countdownCircle}
              viewBox="0 0 24 24"
              width="22"
              height="22"
              aria-hidden="true"
            >
              <circle
                className={styles.countdownCircleTrack}
                cx="12"
                cy="12"
                r={radius}
                fill="none"
              />
              <circle
                className={styles.countdownCircleProgress}
                cx="12"
                cy="12"
                r={radius}
                fill="none"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
              />
            </svg>
            <span className={styles.countdownLabel}>Expires in</span>
            <span className={styles.countdownValue}>
              {formatCountdown(remainingSeconds)}
            </span>
          </div>
        </div>
      ) : null}

      {!code || remainingSeconds <= 0 ? (
        <button
          type="button"
          className={styles.generateButton}
          onClick={onGenerate}
          disabled={regenerating}
        >
          {generateLabel}
        </button>
      ) : null}
    </section>
  );
};

SignInCodePanel.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  code: PropTypes.string,
  expiresInSeconds: PropTypes.number,
  generateLabel: PropTypes.string,
  regenerating: PropTypes.bool,
  onGenerate: PropTypes.func,
  onCopy: PropTypes.func,
  onExpire: PropTypes.func,
  copied: PropTypes.bool,
};

export default SignInCodePanel;
